"use client";
import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import FormatDate from "@/utils/FormatDate";
import BlogCard_Thumbnail from "@/assets/image/BlogCard_Thumbnail.jpg";
import { getReadingTime } from "@/helpers/readingTime";


export default function RelatedBlogs({ blogs, title }) {
  if (!blogs || blogs.length === 0) {
    return null;
  }

  const relatedBlogs = blogs.slice(0, 10);

  const readingTime = (blog) =>
    getReadingTime(
      [blog.title, blog.description]
        .filter(Boolean)
        .join(" ")
    );
  // console.log("Blogs", blog)

  return (
    <div className="py-[16px] lg:border lg:border-[#D7DFE6] lg:px-[28px] lg:py-[16px]">

      {/* Exactly ONE H2 Heading */}
      <h2 className="text-[18px] font-bold leading-[40px] tracking-[0] align-middle mb-[12px] lg:mb-[10px] text-[#202124]">
        {title}
      </h2>

      {/* MOBILE VIEW: Swiper Carousel */}
      <div className="block lg:hidden">
        <Swiper
          modules={[Pagination]}
          spaceBetween={20}
          slidesPerView={1.2}
          className="related-blogs-swiper"
        >
          {relatedBlogs.map((blog) => (
            <SwiperSlide key={blog.id}>
              <BlogCard
                blog={blog}
                isDesktopView={false}
                readingTimeText={readingTime(blog)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* ── DESKTOP VIEW: Stacked Row List ── */}
      <div className="hidden lg:flex lg:flex-col lg:gap-[18px] lg:mt-[10px]  pr-[10px]">
        {relatedBlogs.map((blog, index, arr) => (
          <div key={blog.id} className="flex flex-col gap-[18px]">
            <BlogCard
              blog={blog}
              isDesktopView={true}
              readingTimeText={readingTime(blog)}
            />

            {index !== arr.length - 1 && (
              <hr className="border border-[#D7DFE6]" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Reusable Card Component ──
function BlogCard({ blog, isDesktopView, readingTimeText }) {
  const imageSrc = blog.cover?.url || BlogCard_Thumbnail;
  const imageAlt = blog.cover?.alternativeText || blog.title;

  return (
    <Link href={`/blog/${blog.slug}`} className="block group w-full">

      {/* Layout Switcher */}
      <div
        className={`flex ${isDesktopView
          ? "flex-row gap-[18px]"
          : "flex-col gap-0"
          }`}
      >

        {/* Image Frame */}
        <div
          className={`relative ${isDesktopView ? "h-[110px]" : "mb-[12px]"
            }`}
          style={isDesktopView ? { width: "45%" } : undefined}
        >
          <Image
            src={imageSrc}
            alt={imageAlt}
            width={isDesktopView ? 176 : 320}
            height={isDesktopView ? 110 : 200}
            className={
              isDesktopView
                ? "object-cover"
                : "w-full object-cover"
            }
          />
        </div>

        {/* Content Bundle */}
        <div
          className={`flex flex-col ${isDesktopView ? "gap-[12px]" : "gap-0"
            }`}
          style={isDesktopView ? { width: "55%" } : undefined}
        >

          <div className="flex gap-2">

            {/* Sub Categories Tag */}
            <span
              className={`inline-block tracking-[0] leading-[100%] text-[#2563EB] bg-[#DBEAFE]
                 py-[3px] px-[8px] w-fit max-w-[200px] truncate ${isDesktopView
                  ? "text-[14px]"
                  : "font-medium text-sm mb-[10px]"
                }`}
              style={{
                fontFamily: isDesktopView
                  ? "saans-regular"
                  : "saans-medium",
              }}
            >
              {blog.subcategories?.name}
            </span>
          </div>

          {/* Title */}
          <h4
            className={`text-[16px] leading-[24px] text-[#202124] group-hover:text-[#005D82] transition-colors ${isDesktopView
              ? "line-clamp-3 font-[600px] tracking-normal"
              : "tracking-[0] mb-[8px]"
              }`}
            style={{ fontFamily: "saans-medium" }}
          >
            {blog.title}
          </h4>
        </div>
      </div>

      {/* Metadata Row */}
      <div
        className={`flex items-center gap-[8px] ${isDesktopView ? "mt-[15px]" : ""
          }`}
      >
        {isDesktopView ? (
          <>
            <p
              className="text-[12px] leading-[14.4px] tracking-normal text-[#63778B]"
              style={{ fontFamily: "saans-medium" }}
            >
              {blog.author?.name || "Anonymous"}
            </p>

            <span className="w-[3px] h-[3px] bg-[#63778B] rounded-full"></span>

            <p
              className="text-[12px] leading-[14.4px] tracking-normal text-[#63778B]"
              style={{ fontFamily: "saans-medium" }}
            >
              {FormatDate(blog.updatedAt || blog.blogDate)}
            </p>

            <span className="w-[3px] h-[3px] bg-[#63778B] rounded-full"></span>

            <p
              className="text-[12px] leading-[14.4px] tracking-normal text-[#63778B]"
              style={{ fontFamily: "saans-medium" }}
            >
              {readingTimeText}
            </p>
          </>
        ) : (
          <>
            <span
              className="text-[14px] leading-[100%] tracking-[0] text-[#63778B]"
              style={{ fontFamily: "saans-regular" }}
            >
              {blog.author?.name || "Anonymous"}
            </span>

            <span className="w-[4px] h-[4px] bg-[#D7DFE6] rounded-full"></span>

            <span
              className="text-[14px] leading-[100%] tracking-[0] text-[#63778B]"
              style={{ fontFamily: "saans-regular" }}
            >
              {FormatDate(blog.updatedAt || blog.blogDate)}
            </span>

            <span className="w-[4px] h-[4px] bg-[#D7DFE6] rounded-full"></span>

            <span
              className="text-[14px] leading-[100%] tracking-[0] text-[#63778B]"
              style={{ fontFamily: "saans-regular" }}
            >
              {readingTimeText}
            </span>
          </>
        )}
      </div>
    </Link>
  );
}